import React from "react";

import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { useStyles } from "@/hooks/useStyles";
import { DeviceDataToUpload } from "@/models/DeviceDataToUpload";
import { Queries } from "@/queries/Queries";
import { Theme } from "@/theme/CreateTheme.ts";
import { TextToast } from "@/uiKit/atoms/toast/TextToast";
import { OMNIPOD_DEVICE_CARD_ID, OmnipodDeviceCard } from "@/uiKit/organisms/OmnipodDeviceCard";
import { TANDEM_DEVICE_CARD_ID, TandemDeviceCard } from "@/uiKit/organisms/TandemDeviceCard";
import { Progress } from "@/utils/ProgressMixin";

export const DeviceSelectionList: React.FC = ({}) => {
  const styles = useStyles(makeStyles);
  const [progress, setProgress] = React.useState<Progress | undefined>(undefined);
  const { t } = useTranslation();
  const uploadDeviceData = Queries.device.useUploadDeviceData();

  const onRawData = () => {
    return false;
  };

  const onData = (data: DeviceDataToUpload) => {
    toast.dismiss(progress?.progressId);

    uploadDeviceData.mutate(data, {
      onSuccess: () => {
        TextToast.success(t("devices.uploadSuccess"));
      },
      onError: () => {
        TextToast.error(t("devices.uploadError"), t("devices.uploadErrorDescription"));
      },
      onSettled: () => {
        setProgress(undefined);
      },
    });
  };

  const onError = (message?: string) => {
    toast.dismiss(progress?.progressId);
    TextToast.error(t("devices.uploadError"), message ?? t("devices.uploadErrorDescription"));
    setProgress(undefined);
  };

  const progressListener = (progress: Progress) => {
    if (progress.status === "paused") {
      TextToast.warning(t("devices.uploadPaused"), progress.pauseReason ? t(progress.pauseReason) : undefined, {
        toastId: progress.progressId,
        progress: 1,
        closeButton: false,
        autoClose: false,
        draggable: false,
        closeOnClick: false,
      });
    } else {
      toast.dismiss(progress.progressId);
    }
    setProgress(progress);
  };

  return (
    <div className={styles.container}>
      <TandemDeviceCard
        onRawData={onRawData}
        onData={onData}
        onError={onError}
        progress={progress?.progressId === TANDEM_DEVICE_CARD_ID ? progress : undefined}
        progressListener={progressListener}
      />
      <OmnipodDeviceCard
        onRawData={onRawData}
        onData={onData}
        onError={onError}
        progress={progress?.progressId === OMNIPOD_DEVICE_CARD_ID ? progress : undefined}
        progressListener={progressListener}
      />
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(4)};
    padding: ${theme.spacing(16, 32)};
  `,
});
